import React, { useState, useEffect } from 'react';
import { Save, Wifi, WifiOff, CheckCircle, XCircle, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import { backupService, NASConfig } from '../../services/backupService';

const NASConfigForm: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<Partial<NASConfig>>({
    protocol: 'smb',
    host: '',
    port: 445,
    share_name: '',
    username: '',
    password: '',
    mount_path: '/mnt/nas-backup',
    backup_minio: true,
    backup_database: true,
    backup_media: false,
    enabled: false,
  });
  const [existingConfig, setExistingConfig] = useState<NASConfig | null>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const configs = await backupService.getNASConfigs();
      if (configs.length > 0) {
        setExistingConfig(configs[0]);
        setConfig(configs[0]);
      }
    } catch (error) {
      console.error('Failed to load config:', error);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof NASConfig, value: string | boolean | number) => {
    setConfig({ ...config, [field]: value });

    // Auto-update port when protocol changes
    if (field === 'protocol') {
      setConfig({
        ...config,
        protocol: value as 'smb' | 'nfs',
        port: value === 'smb' ? 445 : 2049,
      });
    }
  };

  const handleTestConnection = async () => {
    if (!existingConfig?.id) {
      toast.error('Please save configuration first');
      return;
    }

    setTesting(true);

    try {
      const result = await backupService.testNASConnection(existingConfig.id);
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }

      // Reload config to get updated test status
      await loadConfig();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      if (existingConfig?.id) {
        await backupService.updateNASConfig(existingConfig.id, config);
      } else {
        await backupService.createNASConfig(config);
      }

      await loadConfig();
      toast.success('Configuration saved successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900">NAS Connection Settings</h3>

      {/* Protocol Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Protocol</label>
        <div className="flex space-x-4">
          <label className="flex items-center">
            <input
              type="radio"
              checked={config.protocol === 'smb'}
              onChange={() => handleChange('protocol', 'smb')}
              className="mr-2"
            />
            <span className="text-sm">SMB/CIFS (Windows/Synology/QNAP)</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              checked={config.protocol === 'nfs'}
              onChange={() => handleChange('protocol', 'nfs')}
              className="mr-2"
            />
            <span className="text-sm">NFS (Unix/Linux)</span>
          </label>
        </div>
      </div>

      {/* Host & Port */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Host</label>
          <input
            type="text"
            placeholder="192.168.1.100 or nas.example.com"
            value={config.host}
            onChange={(e) => handleChange('host', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="mt-1 text-xs text-gray-500">NAS IP address or hostname</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Port</label>
          <input
            type="number"
            value={config.port}
            onChange={(e) => handleChange('port', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="mt-1 text-xs text-gray-500">
            Default: {config.protocol === 'smb' ? '445' : '2049'}
          </p>
        </div>
      </div>

      {/* Share Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Share Name / Export Path
        </label>
        <input
          type="text"
          placeholder={config.protocol === 'smb' ? 'pis-backup' : '/volume1/backup'}
          value={config.share_name}
          onChange={(e) => handleChange('share_name', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="mt-1 text-xs text-gray-500">
          {config.protocol === 'smb' ? 'SMB share name' : 'NFS export path'}
        </p>
      </div>

      {/* Authentication (SMB only) */}
      {config.protocol === 'smb' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
            <input
              type="text"
              value={config.username}
              onChange={(e) => handleChange('username', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">Leave blank for guest access</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={config.password}
              onChange={(e) => handleChange('password', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">Leave blank for guest access</p>
          </div>
        </div>
      )}

      {/* Mount Path */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Mount Path</label>
        <input
          type="text"
          value={config.mount_path}
          onChange={(e) => handleChange('mount_path', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="mt-1 text-xs text-gray-500">Local directory where NAS will be mounted</p>
      </div>

      {/* Backup Options */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Backup Options</label>
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.backup_minio}
              onChange={(e) => handleChange('backup_minio', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Backup MinIO Data (14 GB)</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.backup_database}
              onChange={(e) => handleChange('backup_database', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Backup PostgreSQL Database (71 MB)</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.backup_media}
              onChange={(e) => handleChange('backup_media', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Backup Media Files - Hot Storage (27 GB)</span>
          </label>
        </div>
      </div>

      {/* Enable Checkbox */}
      <div>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => handleChange('enabled', e.target.checked)}
            className="mr-2"
          />
          <span className="text-sm font-medium">Enable automatic backup</span>
        </label>
      </div>

      {/* Status Display */}
      {existingConfig && (
        <div className="flex space-x-2">
          {existingConfig.is_mounted ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
              <CheckCircle className="w-4 h-4 mr-1" />
              Mounted
            </span>
          ) : (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
              <XCircle className="w-4 h-4 mr-1" />
              Not Mounted
            </span>
          )}
          {existingConfig.last_test && (
            existingConfig.last_test_success ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                <Wifi className="w-4 h-4 mr-1" />
                Last Test: Success
              </span>
            ) : (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-100 text-red-800">
                <WifiOff className="w-4 h-4 mr-1" />
                Last Test: Failed
              </span>
            )
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-3 pt-4">
        <button
          onClick={handleTestConnection}
          disabled={testing || !existingConfig}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {testing ? (
            <Loader className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Wifi className="w-4 h-4 mr-2" />
          )}
          {testing ? 'Testing...' : 'Test Connection'}
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <Loader className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>
    </div>
  );
};

export default NASConfigForm;
